import { capitalizeFirstLetter } from '@packages/utils/capitalizeFirstLetter';
import { onlyUniqueStringArray } from '@packages/utils/onlyUniqueStringArray';
import { devConfig } from '../../../../dev/dev.config';
import { devDeps } from '../../../../dev/devDeps';
import { type AppHostNames, appHostMfs, appHosts } from '../../appHosts';
import {
	appReviewHost,
	appVersionsFileName,
	appVersionsHost,
	MicroFrontendsHosts,
} from '../../hosts';
import { MFFolders } from '../../MFFolders';
import { MFNames, type MFNamesKeys } from '../../MFNames';
import { Stands, type StandsKeys } from '../../Stands';
import { generateDevWithTaskNameMFLinks } from '../../utils/generateDevWithTaskNameMFLinks';
import { isHost } from '../../utils/isHost';
import { commonServiceApis, type commonServiceKeysApis } from '../commonApis';
import { defaultAppConfig } from '../default.app.config';
import type {
	AppData,
	AppEnvs,
	MFLinks,
	ServiceStandHost,
	Services,
	ServiceWorker,
} from '../types';

const defaultPort = 3000;
export type GetWebStaticFilePath = (
	type: 'pdfs' | 'icons' | 'illustrations',
	fileKey: string,
) => string;
export class AppConfig<ServiceNames extends Record<string, string>> {
	private readonly defaultPort = defaultPort + Object.keys(appHostMfs).length;
	private readonly appData: AppData<ServiceNames>;
	private readonly useLocal: readonly MFNamesKeys[];

	constructor(props: {
		servicesHosts: ServiceStandHost<ServiceNames>;
		stand: StandsKeys;
		appHostName: AppHostNames;
		serviceWorkerVersion: string;
		daDataPublicKey: string;
		captchaPublicKey: string;
		juicyScoreKey: string;
		juicyScoreHost: string;
		pixelHost: string;
		publicPath?: string;
	}) {
		const {
			stand,
			appHostName,
			publicPath,
			serviceWorkerVersion,
			captchaPublicKey,
			daDataPublicKey,
			servicesHosts,
			juicyScoreKey,
			juicyScoreHost,
			pixelHost,
		} = props;
		let serviceWorkerFileName = `service-worker-${stand}`;
		const taskName = process.env.taskName;
		if (process.env.NODE_ENV === 'development') {
			serviceWorkerFileName = 'service-worker';
		}
		if (taskName) {
			serviceWorkerFileName = `service-worker-${taskName}-${stand}`;
		}

		const microFrontendsHosts = MicroFrontendsHosts[stand];
		this.useLocal =
			process.env.NODE_ENV === 'development'
				? devConfig.useLocalMfs || []
				: [];
		this.appData = {
			taskName: process.env.taskName,
			host:
				process.env.NODE_ENV === 'development' &&
				this.useLocal?.includes(appHostName)
					? this.getLocalHost(appHostName)
					: appHosts[appHostName][stand],
			stand,
			appName: appHostName,
			captchaPublicKey,
			juicyScoreKey,
			juicyScoreHost,
			pixelHost,
			daDataPublicKey,
			baseUrl: taskName ? `/${taskName}/${MFFolders[appHostName]}` : '',
			rootPath: taskName ? '//' : '/',
			publicPath: this.getPublicPath(
				appHostName,
				publicPath || '/',
				stand,
				process.env.taskName,
			),
			serviceWorker: {
				version: serviceWorkerVersion,
				fileName: serviceWorkerFileName,
			},
			appVersionsUrl: appVersionsHost[stand],
			appReviewVersionsUrl: `${appReviewHost[stand]}/${taskName}/${appVersionsFileName}`,
			appReviewPublicPath: appReviewHost[stand],
			api: this.generateServices(stand, servicesHosts),
			webStatic: commonServiceApis.webStaticApi[stand],
			microFrontendsHost: microFrontendsHosts,
			mfLinks: this.generateMFLinks(
				stand,
				microFrontendsHosts,
				appReviewHost[stand],
			),
		};
	}
	public get host() {
		return this.appData.host;
	}
	public get stand() {
		return this.appData.stand;
	}
	public get baseUrl() {
		return this.appData.baseUrl;
	}
	public get rootPath() {
		return this.appData.rootPath;
	}
	public get publicPath() {
		return this.appData.publicPath;
	}
	public get taskName() {
		return this.appData.taskName;
	}
	public get appReviewPublicPath() {
		return this.appData.appReviewPublicPath;
	}
	public get serviceWorker() {
		return this.appData.serviceWorker;
	}
	public get services() {
		return this.appData.serviceWorker;
	}
	public get appReviewVersionsUrl() {
		return this.appData.appVersionsUrl;
	}
	public get appVersionsUrl() {
		return this.appData.appReviewVersionsUrl;
	}
	public get juicyScoreHost() {
		return this.appData.juicyScoreHost;
	}
	public get juicyScoreKey() {
		return this.appData.juicyScoreKey;
	}
	public get api() {
		return this.appData.api;
	}
	public get appName() {
		return this.appData.appName;
	}
	public get webStatic() {
		return this.appData.webStatic;
	}
	public get webStaticSwaggerUrl() {
		return `${this.appData.webStatic}/swagger/json`;
	}
	public get microFrontendsHost() {
		return this.appData.microFrontendsHost;
	}
	public get mfLinks() {
		return this.appData.mfLinks;
	}
	public get appEnvs() {
		return omit(this.appData, [
			'api',
			'serviceWorker',
			'mfLinks',
			'taskName',
			'baseUrl',
		]);
	}
	public getApiService(service: string) {
		return this.appData.api[service];
	}
	public getApiServicesUrl() {
		return onlyUniqueStringArray(
			Object.values(this.appData.api).map((service) => service.basePath),
		);
	}

	public getMfLink(mfLink: MFNamesKeys) {
		return this.appData.mfLinks[mfLink];
	}

	public getServiceWorkerInfo(): ServiceWorker {
		return this.appData.serviceWorker;
	}

	public getAppEnv(envName: AppEnvs): string {
		const allowed = omit(this.appData, [
			'api',
			'serviceWorker',
			'mfLinks',
			'taskName',
			'baseUrl',
		]);

		return allowed[envName];
	}

	public getWebStaticFilePath: GetWebStaticFilePath = (
		type: 'pdfs' | 'icons' | 'illustrations',
		fileKey: string,
	) => {
		return `${this.appData.webStatic}/${type}/${fileKey}`;
	};
	private getPublicPath(
		appHostName: AppHostNames,
		publicPath: string,
		stand: StandsKeys,
		taskName?: string,
	): string {
		if (process.env.taskName) {
			return `${appReviewHost[stand]}/${taskName}/${MFFolders[appHostName]}/`;
		}
		return publicPath;
	}
	private getLocalHost(appHostName: AppHostNames) {
		const port = defaultPort + Object.keys(appHostMfs).indexOf(appHostName);
		const host = getDevHost();
		return `http://${host}:${port}`;
	}
	private generateMFLinks(
		stand: StandsKeys,
		microFrontendsHosts: string,
		appReviewHost: string,
	): MFLinks {
		const mfNames = Object.keys(MFNames) as MFNamesKeys[];
		let mfLinks = {} as MFLinks;
		// if (global?.location?.origin && !/\d\d\d\d$/.test(global.location.origin)) {
		// 	hostUrl =
		// 		Hosts[stand].find((hostUrl) => hostUrl.includes(global.location.origin)) ||
		// 		Hosts[stand][0];
		// }

		for (const mfName of mfNames) {
			const mfFolder = MFFolders[mfName];
			const changes = process.env.changes
				? (JSON.parse(process.env.changes) as string[])
				: [];
			if (
				process.env.taskName &&
				changes.length &&
				changes.includes(MFFolders[mfName])
			) {
				mfLinks[mfName] =
					`${appReviewHost}/${process.env.taskName}/${MFFolders[mfName]}`;
			} else {
				if (isHost(mfName)) {
					mfLinks[mfName] = appHosts[mfName as AppHostNames][stand];
				} else {
					mfLinks[mfName] = `${microFrontendsHosts}/${mfFolder}`;
				}
			}
		}
		if (process.env.NODE_ENV === 'development') {
			if (devConfig.useTaskNameMfs?.length && devConfig.taskName) {
				mfLinks = this.generateDevMFLinks(
					generateDevWithTaskNameMFLinks(mfLinks, appReviewHost),
				);
			} else {
				mfLinks = this.generateDevMFLinks(mfLinks);
			}
		}

		return mfLinks;
	}
	private generateDevMFLinks(mfLinks: MFLinks): MFLinks {
		const useLocalMfs: MFNamesKeys[] = [...devConfig.useLocalMfs];
		for (const mfName in devDeps) {
			for (const depMfName of devDeps[mfName]) {
				if (!useLocalMfs.includes(depMfName)) {
					useLocalMfs.push(depMfName);
				}
			}
		}

		return useLocalMfs.reduce(
			(overrideMFLinks, useLocalMfName, currentIndex) => {
				const host = getDevHost();
				let port = this.defaultPort + currentIndex;

				if (isHost(useLocalMfName)) {
					port =
						defaultPort +
						Object.keys(appHostMfs).indexOf(useLocalMfName);
				}

				return Object.assign(overrideMFLinks, {
					[useLocalMfName]: `http://${host}:${port}`,
				});
			},
			{ ...mfLinks },
		);
	}

	private generateServices<
		Stand extends StandsKeys,
		ServicesHosts extends ServiceStandHost<Record<string, string>>,
		ServiceNames extends Record<string, string>,
	>(stand: Stand, servicesHosts: ServicesHosts): Services<ServiceNames> {
		const services = {} as Services<ServiceNames>;
		for (const serviceName in servicesHosts) {
			const basePath = servicesHosts[serviceName][stand];
			const proxyBasePath = `/api${capitalizeFirstLetter(serviceName)}`;
			services[serviceName] = {
				basePath:
					process.env.NODE_ENV === 'development'
						? proxyBasePath
						: basePath,
				proxyBasePath:
					process.env.NODE_ENV === 'development'
						? basePath
						: proxyBasePath,
			};
		}
		return services;
	}
}

export class AppConfigs<ServiceNames extends Record<string, string>> {
	public readonly test: AppConfig<
		ServiceNames & typeof commonServiceKeysApis
	>;
	public readonly cert: AppConfig<
		ServiceNames & typeof commonServiceKeysApis
	>;
	public readonly prod: AppConfig<
		ServiceNames & typeof commonServiceKeysApis
	>;
	constructor(props: {
		servicesHosts: ServiceStandHost<ServiceNames>;
		appHostName: AppHostNames;
		publicPath?: string;
	}) {
		const { appHostName, publicPath = '/' } = props;
		const servicesHosts = {
			...props.servicesHosts,
			...commonServiceApis,
		};
		this.test = new AppConfig<ServiceNames>({
			...defaultAppConfig[Stands.test],
			servicesHosts,
			appHostName,
			publicPath,
			stand: Stands.test,
		});
		this.cert = new AppConfig<ServiceNames>({
			...defaultAppConfig[Stands.cert],
			servicesHosts,
			appHostName,
			publicPath,
			stand: Stands.cert,
		});
		this.prod = new AppConfig<ServiceNames>({
			...defaultAppConfig[Stands.prod],
			servicesHosts,
			appHostName,
			publicPath,
			stand: Stands.prod,
		});
	}
}
export function omit<T, K extends keyof T>(
	obj: T,
	keys: readonly K[],
): Omit<T, K> {
	return Object.fromEntries(
		Object.entries(obj as object).filter(
			([key]) => !keys.includes(key as K),
		),
	) as Omit<T, K>;
}

function getDevHost() {
	if (devConfig.useExternalIP) {
		// if (typeof globalThis !== 'undefined' && globalThis.window) {
		// 	return window.location.hostname;
		// }
		return devConfig.externalIp;
	}
	return 'localhost';
}
